import * as React from 'react';
import { useState, useEffect } from 'react';
import { NT, NI } from './NaomiTheme';
import type { SelectOption, MenuItem as MenuItemType } from './types/NaomiUiTypes';

import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import InputAdornment from '@mui/material/InputAdornment';
import MuiMenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import Divider from '@mui/material/Divider';
import ListSubheader from '@mui/material/ListSubheader';
import InputBase from '@mui/material/InputBase';
import Typography from '@mui/material/Typography';

export interface MIProps {
  name: string;
  size?: number;
  color?: string;
  style?: React.CSSProperties;
}

export const MI: React.FC<MIProps> = ({ name, size = 18, color, style }) => (
  <span className="material-icons" style={{ fontSize: size, color, lineHeight: 1, ...style }}>
    {name}
  </span>
);

type FieldSize = 'sm' | 'md';

export interface MTextFieldProps {
  id?: string;
  label?: React.ReactNode;
  value?: string;
  onChange?: (next: string) => void;
  multiline?: boolean;
  rows?: number;
  helper?: React.ReactNode;
  type?: React.HTMLInputTypeAttribute;
  size?: FieldSize;
  adornment?: React.ReactNode;
  disabled?: boolean;
  error?: boolean;
  fullWidth?: boolean;
  autoFocus?: boolean;
  placeholder?: string;
  style?: React.CSSProperties;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement>;
}

export function MTextField({
  id, label, value, onChange, multiline, rows = 1, helper,
  type = 'text', size = 'md', adornment, disabled, error,
  fullWidth = true, autoFocus, placeholder, style, onKeyDown,
}: MTextFieldProps): JSX.Element {
  return (
    <Box sx={{ width: fullWidth ? '100%' : undefined, ...(style ?? {}) }}>
      {label !== undefined && (
        <Box component="div" sx={{
          fontSize: 11.5, fontWeight: 600, color: error ? NT.danger : NT.ink2,
          textTransform: 'uppercase', letterSpacing: 0.4, mb: 0.5,
          fontFamily: NT.font,
        }}>
          {label}
        </Box>
      )}
      <TextField
        id={id}
        value={value ?? ''}
        onChange={(e) => onChange?.(e.target.value)}
        multiline={multiline}
        rows={multiline ? rows : undefined}
        helperText={helper}
        type={type}
        size={size === 'sm' ? 'small' : 'medium'}
        InputProps={adornment !== undefined ? {
          endAdornment: <InputAdornment position="end">{adornment}</InputAdornment>,
        } : undefined}
        disabled={disabled}
        error={error}
        fullWidth
        autoFocus={autoFocus}
        placeholder={placeholder}
        onKeyDown={onKeyDown as any}
        sx={{
          '& .MuiInputBase-input': {
            color: NT.ink,
            fontFamily: NT.font,
            fontSize: size === 'sm' ? 13 : 14,
          },
          '& .MuiInputBase-input::placeholder': { color: NT.ink4, opacity: 1 },
          '& .MuiOutlinedInput-root': {
            '& fieldset':               { borderColor: NT.line2 },
            '&:hover fieldset':         { borderColor: NT.ink3  },
            '&.Mui-focused fieldset':   { borderColor: NT.primary, borderWidth: 1.5 },
            '&.Mui-error fieldset':     { borderColor: NT.danger  },
            '&.Mui-disabled fieldset':  { borderColor: NT.line   },
          },
          '& .MuiFormHelperText-root': { color: error ? NT.danger : NT.ink3, fontFamily: NT.font },
          '& .MuiInputAdornment-root': { color: NT.ink3 },
        }}
      />
    </Box>
  );
}

export interface MSelectProps<V extends string = string> {
  id?: string;
  label?: React.ReactNode;
  value?: V;
  onChange?: (next: V) => void;
  options: SelectOption<V>[];
  size?: FieldSize;
  helper?: React.ReactNode;
  fullWidth?: boolean;
}

export function MSelect<V extends string = string>({
  id, label, value, onChange, options, size = 'md', helper, fullWidth = true,
}: MSelectProps<V>): JSX.Element {
  return (
    <Box sx={{ width: fullWidth ? '100%' : undefined }}>
      {label !== undefined && (
        <Box component="div" sx={{
          fontSize: 11.5, fontWeight: 600, color: NT.ink2,
          textTransform: 'uppercase', letterSpacing: 0.4, mb: 0.5,
          fontFamily: NT.font,
        }}>
          {label}
        </Box>
      )}
      <TextField
        select
        id={id}
        value={value ?? ''}
        onChange={(e) => onChange?.(e.target.value as V)}
        size={size === 'sm' ? 'small' : 'medium'}
        helperText={helper}
        fullWidth
        sx={{
          '& .MuiSelect-select, & .MuiInputBase-input': {
            color: NT.ink,
            fontFamily: NT.font,
            fontSize: size === 'sm' ? 13 : 14,
          },
          '& .MuiOutlinedInput-root': {
            '& fieldset':             { borderColor: NT.line2 },
            '&:hover fieldset':       { borderColor: NT.ink3  },
            '&.Mui-focused fieldset': { borderColor: NT.primary, borderWidth: 1.5 },
          },
          '& .MuiSvgIcon-root': { color: NT.ink3 },
          '& .MuiFormHelperText-root': { color: NT.ink3, fontFamily: NT.font },
        }}
      >
        {options.map((o) => (
          <MuiMenuItem
            key={o.value}
            value={o.value}
            sx={{ fontSize: 13.5, color: NT.ink, fontFamily: NT.font }}
          >
            {o.label}
          </MuiMenuItem>
        ))}
      </TextField>
    </Box>
  );
}

export interface MComboboxProps<V extends string = string> {
  id?: string;
  label?: React.ReactNode;
  value?: V;
  onChange?: (next: V) => void;
  options: SelectOption<V>[];
  size?: FieldSize;
  helper?: React.ReactNode;
  fullWidth?: boolean;
  placeholder?: string;
}

export function MCombobox<V extends string = string>({
  id, label, value, onChange, options, size = 'md', helper, fullWidth = true, placeholder,
}: MComboboxProps<V>): JSX.Element {
  const selected = options.find(o => o.value === value) ?? null;
  return (
    <Box sx={{ width: fullWidth ? '100%' : undefined }}>
      {label !== undefined && (
        <Box component="div" sx={{
          fontSize: 11.5, fontWeight: 600, color: NT.ink2,
          textTransform: 'uppercase', letterSpacing: 0.4, mb: 0.5,
          fontFamily: NT.font,
        }}>
          {label}
        </Box>
      )}
      <Autocomplete
        id={id}
        options={options}
        value={selected}
        onChange={(_, opt) => onChange?.((opt?.value ?? '') as V)}
        getOptionLabel={(o) => o.label}
        isOptionEqualToValue={(a, b) => a.value === b.value}
        size={size === 'sm' ? 'small' : 'medium'}
        fullWidth
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholder}
            helperText={helper}
            sx={{
              '& .MuiInputBase-input': {
                color: NT.ink,
                fontFamily: NT.font,
                fontSize: size === 'sm' ? 13 : 14,
              },
              '& .MuiOutlinedInput-root': {
                '& fieldset':             { borderColor: NT.line2 },
                '&:hover fieldset':       { borderColor: NT.ink3  },
                '&.Mui-focused fieldset': { borderColor: NT.primary, borderWidth: 1.5 },
              },
              '& .MuiSvgIcon-root': { color: NT.ink3 },
              '& .MuiFormHelperText-root': { color: NT.ink3, fontFamily: NT.font },
            }}
          />
        )}
        renderOption={(props, option) => (
          <MuiMenuItem {...props as any} key={option.value} sx={{ fontSize: 13.5, color: NT.ink, fontFamily: NT.font }}>
            {option.label}
          </MuiMenuItem>
        )}
      />
    </Box>
  );
}

export interface MSwitchProps {
  checked?: boolean;
  onChange?: (next: boolean) => void;
  label?: React.ReactNode;
  id?: string;
}

export function MSwitch({ checked = false, onChange, label, id }: MSwitchProps): JSX.Element {
  return (
    <FormControlLabel
      control={
        <Switch
          id={id}
          checked={checked}
          onChange={(e) => onChange?.(e.target.checked)}
          size="small"
        />
      }
      label={
        label !== undefined
          ? <Typography variant="body2" sx={{ color: NT.ink2 }}>{label}</Typography>
          : ''
      }
      sx={{ ml: 0, gap: 0.5 }}
    />
  );
}

export interface MDialogProps {
  id?: string;
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  width?: number;
  children?: React.ReactNode;
  actions?: React.ReactNode;
  icon?: React.ReactNode;
}

export function MDialog({
  id, open, onClose, title, subtitle, width = 560, children, actions, icon,
}: MDialogProps): JSX.Element {
  return (
    <Dialog
      id={id ?? 'naomi-ui-dialog-base'}
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width,
          maxWidth: 'calc(100vw - 40px)',
          maxHeight: 'calc(100vh - 60px)',
          borderRadius: '12px',
        },
      }}
    >
      <DialogTitle
        sx={{
          p: '18px 22px 14px',
          borderBottom: `1px solid ${NT.line}`,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.75,
        }}
      >
        {icon !== undefined && (
          <Box sx={{
            width: 36, height: 36, borderRadius: 1,
            bgcolor: NT.primarySoft, color: NT.primary2,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            {icon}
          </Box>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: NT.ink, lineHeight: 1.35 }}>
            {title}
          </Typography>
          {subtitle !== undefined && (
            <Typography sx={{ fontSize: 12.5, color: NT.ink3, mt: 0.25 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <IconButton
          id="naomi-ui-iconbutton-dialog-close"
          onClick={onClose}
          size="small"
          sx={{ mt: -0.5, mr: -1, color: NT.ink3 }}
        >
          <NI.Close />
        </IconButton>
      </DialogTitle>
      <DialogContent className="naomi-scroll" sx={{ p: '18px 22px' }}>
        {children}
      </DialogContent>
      {actions !== undefined && (
        <DialogActions
          sx={{
            p: '12px 22px',
            borderTop: `1px solid ${NT.line}`,
            bgcolor: NT.surfaceAlt,
            justifyContent: 'flex-end',
            gap: 1,
          }}
        >
          {actions}
        </DialogActions>
      )}
    </Dialog>
  );
}

export interface MMenuProps {
  anchor: HTMLElement | null;
  onClose: () => void;
  items: MenuItemType[];
}

export function MMenu({ anchor, onClose, items }: MMenuProps): JSX.Element {
  return (
    <Menu
      anchorEl={anchor}
      open={Boolean(anchor)}
      onClose={onClose}
      PaperProps={{
        sx: {
          minWidth: 200,
          borderRadius: 1,
          border: `1px solid ${NT.line}`,
          boxShadow: NT.shadow3,
          py: 0.5,
        },
      }}
    >
      {items.map((it, i) => {
        if (it.divider) {
          return <Divider key={i} sx={{ my: 0.5 }} />;
        }
        if (it.header !== undefined) {
          return (
            <ListSubheader
              key={i}
              disableSticky
              sx={{
                fontSize: 10.5, fontWeight: 700, color: NT.ink4,
                textTransform: 'uppercase', letterSpacing: 0.5,
                lineHeight: '28px', py: 0, px: 1.25,
                bgcolor: 'transparent',
              }}
            >
              {it.header}
            </ListSubheader>
          );
        }
        return (
          <MuiMenuItem
            key={i}
            onClick={() => { it.onClick?.(); onClose(); }}
            disabled={it.disabled}
            sx={{
              fontSize: 13,
              fontWeight: 500,
              color: it.danger ? NT.danger : NT.ink2,
              minHeight: 'unset',
              py: '7px',
              px: 1.25,
              gap: 1.25,
              '&:hover': {
                bgcolor: it.danger ? NT.dangerSoft : NT.surfaceAlt,
              },
            }}
          >
            {it.icon !== undefined && (
              <Box
                component="span"
                sx={{ color: it.danger ? NT.danger : NT.ink3, display: 'inline-flex' }}
              >
                {it.icon}
              </Box>
            )}
            <Box component="span" sx={{ flex: 1 }}>{it.label}</Box>
            {it.shortcut !== undefined && (
              <Typography
                component="span"
                sx={{ fontSize: 11, color: NT.ink4, fontFamily: NT.fontMono }}
              >
                {it.shortcut}
              </Typography>
            )}
          </MuiMenuItem>
        );
      })}
    </Menu>
  );
}

export interface InlineEditProps {
  value: string;
  onChange?: (next: string) => void;
  fontSize?: number;
  fontWeight?: React.CSSProperties['fontWeight'];
  color?: string;
  hint?: string;
  placeholder?: string;
  multiline?: boolean;
}

export function InlineEdit({
  value, onChange,
  fontSize = 14, fontWeight = 600, color = NT.ink,
  hint = 'Click to rename', placeholder, multiline = false,
}: InlineEditProps): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  useEffect(() => { setDraft(value); }, [value]);

  const commit = (): void => {
    setEditing(false);
    if (draft !== value) onChange?.(draft);
  };

  if (editing) {
    return (
      <InputBase
        autoFocus
        multiline={multiline}
        rows={multiline ? 2 : undefined}
        value={draft}
        placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Escape') { setDraft(value); setEditing(false); }
          if (e.key === 'Enter' && !multiline) commit();
          if (e.key === 'Enter' && multiline && (e.metaKey || e.ctrlKey)) commit();
        }}
        sx={{
          fontSize, fontWeight, color, width: '100%',
          border: `1px solid ${NT.primary}`,
          borderRadius: '4px',
          px: '6px', py: '2px',
          '& textarea': { resize: 'vertical' },
        }}
      />
    );
  }

  return (
    <span
      onClick={(e) => { e.stopPropagation(); setEditing(true); }}
      title={hint}
      style={{
        fontSize, fontWeight, color, cursor: 'text',
        padding: '2px 6px', borderRadius: 4,
        display: 'inline-block', minWidth: 60,
        transition: 'background .12s',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = NT.surfaceAlt; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
    >
      {value || (
        <span style={{ color: NT.ink4, fontStyle: 'italic' }}>
          {placeholder ?? 'Untitled'}
        </span>
      )}
    </span>
  );
}

Object.assign(window, { MI, MTextField, MSelect, MSwitch, MDialog, MMenu, InlineEdit });
